import { useState } from 'react';
import { FormDialog, StatusChip } from '@nyce/admin-kit/console';
import { Button, Dialog, Switch, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox, Loading, TableScroller, TagsField, useAsync, useInterval } from '../../components/common';
import { formatCount, formatDateTime } from '../../lib/format';
import { isRunActive, prodStats, type RunDatabase, type Schedule } from '../api/prodstats';
import { RunChip } from '../shared';

const DB_TONE = { ok: 'green', error: 'red', skipped: 'grey' } as const;

/** One run: its state and every database with the copy it was counted on, errors first. */
export function RunDialog({
  id,
  canCancel,
  onClose,
  onChanged,
}: {
  id: number;
  canCancel: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const res = useAsync(() => prodStats.run(id), [id]);
  const run = res.data?.run;
  useInterval(() => void res.reload(), 3000, isRunActive(run));
  const [error, setError] = useState<unknown>(null);
  const cancel = async () => {
    try {
      await prodStats.cancel(id);
      await res.reload();
      onChanged();
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Run #${id}`}
      maxWidth={900}
      data-testid="prod-run"
      footer={
        canCancel && isRunActive(run) && !run?.cancelRequested ? (
          <Button variant="danger" onClick={() => void cancel()}>
            Cancel run
          </Button>
        ) : undefined
      }
    >
      <ErrorBox error={res.error || error} />
      {!run ? (
        <Loading loading />
      ) : (
        <>
          <p>
            <RunChip run={run} /> {run.scheduled ? 'Nightly run' : `Started by ${run.createdBy}`} ·{' '}
            {formatDateTime(run.startedAt ?? run.createdAt)} · {run.databasesDone}/{run.databasesTotal}{' '}
            databases · {formatCount(run.rowsCounted)} rows added
            {run.databases.length > 0 && ` · only ${run.databases.join(', ')}`}
          </p>
          {run.error && <p className="nyk-text-red">{run.error}</p>}
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" aria-label={`Databases of run ${id}`}>
              <thead>
                <tr>
                  <th>Database</th>
                  <th>Counted on</th>
                  <th>Status</th>
                  <th className="ps-num">Rows added</th>
                </tr>
              </thead>
              <tbody>
                {(res.data?.databases ?? []).map((d: RunDatabase) => (
                  <tr key={`${d.database}/${d.serverId}`}>
                    <td className="nyk-card-title">
                      {d.database || '—'}
                      {d.backfill && <div className="nyk-ui-hint">first count (history)</div>}
                    </td>
                    <td data-label="Counted on">
                      {d.serverId}
                      {d.standby && <span className="nyk-ui-hint"> · replica</span>}
                    </td>
                    <td data-label="Status">
                      <StatusChip value={d.status} tone={DB_TONE[d.status]} label={d.status} />
                      {d.note && <div className="nyk-ui-hint nyk-wrap">{d.note}</div>}
                    </td>
                    <td data-label="Rows added" className="ps-num">
                      {formatCount(d.rows)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </>
      )}
    </Dialog>
  );
}

/** When the nightly count runs (cron in a zone), whether it runs, and databases left out (e.g. a copy on a production server). */
export function ScheduleDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (s: Schedule) => void;
}) {
  const res = useAsync(() => prodStats.schedule(), []);
  const s = res.data;
  return s ? (
    <ScheduleForm schedule={s} onClose={onClose} onSaved={onSaved} />
  ) : (
    <Dialog open onClose={onClose} title="Nightly count">
      <ErrorBox error={res.error} />
      {!res.error && <Loading loading />}
    </Dialog>
  );
}

function ScheduleForm({
  schedule,
  onClose,
  onSaved,
}: {
  schedule: Schedule;
  onClose: () => void;
  onSaved: (s: Schedule) => void;
}) {
  const [v, setV] = useState({
    cron: schedule.cron,
    timezone: schedule.timezone,
    enabled: schedule.enabled,
    excluded: schedule.excluded,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await prodStats.saveSchedule(v);
      onSaved(saved);
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormDialog
      open
      title="Nightly count"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Save"
      canSubmit={!!v.cron.trim() && !!v.timezone.trim()}
      busy={busy}
      error={error}
      draft={v}
    >
      <TextField
        label="When (cron)"
        name="cron"
        value={v.cron}
        className="nyk-mono"
        hint="minute hour day month weekday, e.g. 0 2 * * * = 02:00 every night"
        onChange={(e) => setV({ ...v, cron: e.target.value })}
      />
      <TextField
        label="Time zone"
        name="timezone"
        value={v.timezone}
        hint="IANA name; also the zone of “today” on the page"
        onChange={(e) => setV({ ...v, timezone: e.target.value })}
      />
      <div className="nyk-span2">
        <Switch
          checked={v.enabled}
          onChange={(enabled) => setV({ ...v, enabled })}
          label={v.enabled ? 'Count every night' : 'Nightly count off (Count now still works)'}
        />
      </div>
      <div className="nyk-span2">
        <TagsField
          label="Leave out these databases"
          value={v.excluded}
          onChange={(excluded) => setV({ ...v, excluded })}
          placeholder="Database name, Enter adds it"
          hint="For a test or restored copy on a production server. What was already counted stays."
        />
      </div>
    </FormDialog>
  );
}
